import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Card,
  DateRangePicker,
  Skeleton,
  type DateRangePreset,
} from '@/components/ui';
import {
  useResellerEarnings,
  type EarningsCustomerRow,
  type ReportRange,
} from '@/hooks/useReports';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

type PresetKey = '30d' | '90d' | 'this_month' | 'last_month' | '12m';

interface Preset {
  key: PresetKey;
  /** i18n key for the visible label. */
  labelKey: string;
  from: string;
  to: string;
}

// Same shape as reports.tsx but with a 12-month default; resellers
// usually want a "this year" view rather than the customer-side
// short-window slice.
function makePresets(now: Date = new Date()): Preset[] {
  const dayMs = 24 * 60 * 60 * 1000;
  const toIso = (d: Date) => d.toISOString();
  const startOfDay = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const startOfMonth = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));

  const today = startOfDay(now);
  const thisMonthStart = startOfMonth(now);
  const lastMonthStart = startOfMonth(
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)),
  );
  const twelveMonthsAgo = startOfMonth(
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1)),
  );

  return [
    { key: '30d', labelKey: 'earn_preset_30d', from: toIso(new Date(today.getTime() - 30 * dayMs)), to: toIso(now) },
    { key: '90d', labelKey: 'earn_preset_90d', from: toIso(new Date(today.getTime() - 90 * dayMs)), to: toIso(now) },
    { key: 'this_month', labelKey: 'earn_preset_this_month', from: toIso(thisMonthStart), to: toIso(now) },
    { key: 'last_month', labelKey: 'earn_preset_last_month', from: toIso(lastMonthStart), to: toIso(thisMonthStart) },
    { key: '12m', labelKey: 'earn_preset_12m', from: toIso(twelveMonthsAgo), to: toIso(now) },
  ];
}

interface KpiProps {
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
}

function Kpi({ label, value, hint, loading }: KpiProps): JSX.Element {
  return (
    <Card padding="md">
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
        {label}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-24 mt-2" />
      ) : (
        <>
          <div className="text-[24px] font-semibold text-text mt-1">{value}</div>
          {hint && <div className="text-[12px] text-text-3 mt-0.5">{hint}</div>}
        </>
      )}
    </Card>
  );
}

// Reads locale from i18n so Spanish users see "abr 25" not "Apr 25".
// The two-digit year + short-month combo stays compact enough for the
// chart axis even in locales with longer month names (e.g. German Mär).
function formatMonthLabel(iso: string, locale: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { year: '2-digit', month: 'short' });
}

function CustomerRow({ row }: { row: EarningsCustomerRow }): JSX.Element {
  // Revenue=0 yields 0% margin (rather than NaN). Negative earnings
  // (cost > revenue) render in red so the reseller spots loss-leaders
  // immediately — the existing text-text-3 was the same shade as
  // healthy customers.
  const margin = row.revenue > 0 ? (row.earnings / row.revenue) * 100 : 0;
  const negative = row.earnings < 0;
  return (
    <tr className="border-b border-line">
      <td className="py-2.5 px-3 text-[13px] text-text">
        <div className="font-medium">{row.name}</div>
        {row.country && <div className="text-[11px] text-text-3">{row.country}</div>}
      </td>
      <td className="py-2.5 px-3 text-[13px] text-text-2 text-right tabular-nums">
        {row.sent.toLocaleString()}
      </td>
      <td className="py-2.5 px-3 text-[13px] text-text-2 text-right tabular-nums">
        {formatCurrency(row.revenue)}
      </td>
      <td className="py-2.5 px-3 text-[13px] text-text-2 text-right tabular-nums">
        {formatCurrency(row.cost)}
      </td>
      <td
        className={cn(
          'py-2.5 px-3 text-[13px] text-right tabular-nums font-medium',
          negative ? 'text-danger' : 'text-text',
        )}
      >
        {formatCurrency(row.earnings)}
      </td>
      <td
        className={cn(
          'py-2.5 px-3 text-[12px] text-right tabular-nums',
          negative ? 'text-danger' : 'text-text-3',
        )}
      >
        {margin.toFixed(1)}%
      </td>
    </tr>
  );
}

export default function AdminEarningsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const locale = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const presets = useMemo(() => makePresets(), []);
  const [activeKey, setActiveKey] = useState<string>('30d');
  const [customRange, setCustomRange] = useState<ReportRange>({
    from: presets[0]!.from,
    to: presets[0]!.to,
  });
  const presetFallback = presets[0]!;
  const activePreset = presets.find((p) => p.key === activeKey);
  const range: ReportRange =
    activeKey === 'custom'
      ? customRange
      : activePreset
        ? { from: activePreset.from, to: activePreset.to }
        : { from: presetFallback.from, to: presetFallback.to };

  const pickerPresets: DateRangePreset[] = presets.map((p) => ({
    key: p.key,
    label: t(p.labelKey),
    range: { from: p.from, to: p.to },
  }));

  const { data, isLoading, error } = useResellerEarnings(range);

  const totals = data?.totals;
  const margin = totals && totals.revenue > 0
    ? (totals.earnings / totals.revenue) * 100
    : 0;

  const monthChartData = useMemo(
    () =>
      (data?.by_month ?? []).map((m) => ({
        month: formatMonthLabel(m.month, locale),
        earnings: m.earnings,
        revenue: m.revenue,
        cost: m.cost,
      })),
    [data?.by_month, locale],
  );

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="display text-[22px] font-semibold tracking-[-0.015em] text-text leading-[1.2] m-0">
            {t('earn_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-0.5">
            {t('earn_subtitle')}
          </p>
        </div>
        <DateRangePicker
          presets={pickerPresets}
          value={activeKey}
          range={range}
          onChange={({ key, range: r }) => {
            setActiveKey(key);
            if (key === 'custom') setCustomRange(r);
          }}
        />
      </div>

      {error && (
        <Card padding="md" className="mb-5">
          <div className="text-[13px] text-danger">
            {t('earn_load_error', { error: error instanceof Error ? error.message : 'unknown' })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <Kpi
          label={t('earn_kpi_revenue')}
          value={formatCurrency(totals?.revenue ?? 0)}
          hint={t('earn_kpi_revenue_hint')}
          loading={isLoading}
        />
        <Kpi
          label={t('earn_kpi_cost')}
          value={formatCurrency(totals?.cost ?? 0)}
          hint={t('earn_kpi_cost_hint')}
          loading={isLoading}
        />
        <Kpi
          label={t('earn_kpi_earnings')}
          value={formatCurrency(totals?.earnings ?? 0)}
          hint={t('earn_kpi_earnings_hint')}
          loading={isLoading}
        />
        <Kpi
          label={t('earn_kpi_margin')}
          value={`${margin.toFixed(1)}%`}
          hint={t('earn_kpi_margin_hint')}
          loading={isLoading}
        />
        <Kpi
          label={t('earn_kpi_messages')}
          value={(totals?.sent ?? 0).toLocaleString()}
          hint={t('earn_kpi_messages_hint', { count: totals?.delivered ?? 0 })}
          loading={isLoading}
        />
        <Kpi
          label={t('earn_kpi_active')}
          value={(totals?.active_customers ?? 0).toLocaleString()}
          hint={t('earn_kpi_active_hint')}
          loading={isLoading}
        />
      </div>

      <Card padding="md" className="mb-5">
        <div className="text-[13px] font-medium text-text mb-4">{t('earn_chart_title')}</div>
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : monthChartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-[13px] text-text-3">
            {t('earn_chart_empty')}
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthChartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: 'var(--text-3)' }}
                  axisLine={{ stroke: 'var(--line)' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-3)' }}
                  axisLine={{ stroke: 'var(--line)' }}
                  tickLine={false}
                  tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  contentStyle={{
                    backgroundColor: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="earnings" fill="var(--volt)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card padding="none">
        <div className="px-4 py-3 border-b border-line">
          <div className="text-[13px] font-medium text-text">{t('earn_table_title')}</div>
        </div>
        {isLoading ? (
          <div className="px-4 py-6"><Skeleton className="h-32 w-full" /></div>
        ) : (data?.customers ?? []).length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-text-3">
            {t('earn_table_empty')}
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-surface">
                <tr className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
                  <th className="py-2 px-3 text-left">{t('earn_col_customer')}</th>
                  <th className="py-2 px-3 text-right">{t('earn_col_sent')}</th>
                  <th className="py-2 px-3 text-right">{t('earn_kpi_revenue')}</th>
                  <th className="py-2 px-3 text-right">{t('earn_kpi_cost')}</th>
                  <th className="py-2 px-3 text-right">{t('earn_kpi_earnings')}</th>
                  <th className="py-2 px-3 text-right">{t('earn_kpi_margin')}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.customers ?? []).map((row) => (
                  <CustomerRow key={row.customer_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
